/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Input,
} from "@chakra-ui/react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useUI } from "../theme/ThemeProvider";
const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj, (key, value) => {
      // Don't skip the root object (where key is empty string)
      if (key !== "" && typeof value === "object" && value !== null) return "";
      return value;
    });
  } catch {
    return "";
  }
};

export default function ResponsiveTable({
  columns,
  data,
  renderRow,
  renderCard,
  searchable = false,
  sortable = true,
  filterOptions = null,
  headerActions = null,
  searchFields = null,
  renderExpandedRow = null,
  selectedRowIndex = null,
  onRowClick = null,
}) {
  const { colors, isArabic, isDark } = useUI();
  const dir = isArabic ? "rtl" : "ltr";

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filterValue, setFilterValue] = useState("");

  /* ================= Filter + Sort ================= */
  const processedData = useMemo(() => {
    let result = [...data];

    if (searchable && searchTerm) {
      result = result.filter((row) => {
        if (Array.isArray(searchFields) && searchFields.length > 0) {
          return searchFields
            .map((field) => row[field])
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }

        // السلوك القديم الافتراضي
        return safeStringify(row)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    }


    if (filterOptions && filterValue !== "") {
      result = result.filter(
        (row) =>
          String(row[filterOptions.field]) === String(filterValue)
      );
    }

    if (sortable && sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === "string" && typeof bVal === "string") {
          const cmp = aVal.localeCompare(bVal, isArabic ? "ar" : "en");
          return sortConfig.direction === "asc" ? cmp : -cmp;
        }

        return sortConfig.direction === "asc"
          ? (aVal || 0) - (bVal || 0)
          : (bVal || 0) - (aVal || 0);
      });
    }

    return result;
  }, [data, searchTerm, filterValue, sortConfig, searchable, sortable, filterOptions, isArabic]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort opacity={0.4} />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <Box>
      {/* ===== Search / Filters / Actions ===== */}
      {(searchable || filterOptions || headerActions) && (
        <Flex
          mb={6}
          gap={4}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
          className="animate-in fade-in slide-in-from-top-4 duration-500"
        >
          {/* ===== Left group (Search + Filters) ===== */}
          <Flex
            gap={3}
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            flex="1"
          >
            {/* Search */}
            {searchable && (
              <Box position="relative" w={{ base: "100%", md: "300px" }}>
                <Input
                  placeholder={isArabic ? "بحث سريع..." : "Quick search..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg={isDark ? "#1E293B" : "white"}
                  border="1px solid"
                  borderColor={isDark ? "#334155" : "#E2E8F0"}
                  _focus={{ borderColor: colors.mainColor, boxShadow: "0 0 0 1px " + colors.mainColor }}
                  borderRadius="xl"
                  height="45px"
                  fontSize="sm"
                />
              </Box>
            )}

            {/* Filters */}
            {filterOptions && (
              <Box
                overflowX="auto"
                className="no-scrollbar"
                maxW={{ base: "100%", md: "none" }}
              >
                <HStack
                  spacing={2}
                  bg={isDark ? "#1E293B" : "#F8FAFC"}
                  p="4px"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={isDark ? "#334155" : "#E2E8F0"}
                  minW="fit-content"
                >
                  {filterOptions.options.map((opt) => {
                    const isActive = String(filterValue) === String(opt.value);

                    return (
                      <Box
                        key={opt.value}
                        px={4}
                        py={2}
                        borderRadius="lg"
                        cursor="pointer"
                        fontSize="xs"
                        fontWeight="700"
                        whiteSpace="nowrap"
                        transition="all 0.2s"
                        bg={isActive ? colors.mainColor : "transparent"}
                        color={isActive ? "#fff" : (isDark ? "#94A3B8" : "#64748B")}
                        _hover={!isActive ? { bg: isDark ? "#334155" : "#EDF2F7", color: colors.mainColor } : {}}
                        onClick={() => setFilterValue(opt.value)}
                      >
                        {opt.label}
                      </Box>
                    );
                  })}
                </HStack>
              </Box>
            )}
          </Flex>

          {/* ===== Right group (Add Button) ===== */}
          {headerActions && (
            <Flex
              justify={{ base: "stretch", lg: "flex-end" }}
              w={{ base: "100%", lg: "auto" }}
            >
              {headerActions}
            </Flex>
          )}
        </Flex>
      )}

      {/* ===== Horizontal Scroll Indicator (Mobile Only) ===== */}
      <Box
        display={{ base: "block", md: "none" }}
        textAlign="center"
        mb={2}
      >
        <span className="text-[10px] text-gray-400 italic">
          {isArabic ? "اسحب لليمين/اليسار لمشاهدة المزيد ←" : "Swipe left/right to see more →"}
        </span>
      </Box>


      {/* ===== Table ===== */}
      <Box
        overflowX="auto"
        bg={colors.cardBg}
        borderRadius="xl"
        border="1px solid"
        borderColor={colors.borderColor}
      >
        <Table variant="simple" minW="600px" dir={dir}>
          <Thead bg={colors.tableHeaderBg}>
            <Tr>
              {columns.map((col, i) => (
                <Th
                  key={i}
                  cursor={col.sortKey ? "pointer" : "default"}
                  onClick={() => col.sortKey && handleSort(col.sortKey)}
                  textAlign={col.align || (isArabic ? "right" : "left")}
                  width={col.width || "auto"}
                  _hover={{ bg: isDark ? "#334155" : "#EDF2F7" }}
                >
                  <Flex
                    width="full"
                    align="center"
                    gap={2}
                    justify={
                      col.align === "center"
                        ? "center"
                        : col.align === "right"
                          ? (isArabic ? "flex-start" : "flex-end")
                          : col.align === "left"
                            ? (isArabic ? "flex-end" : "flex-start")
                            : (isArabic ? "flex-start" : "flex-start")
                    }
                  >
                    {col.header}
                    {col.sortKey && (
                      <Box color={colors.mainColor}>
                        {getSortIcon(col.sortKey)}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>

          <Tbody>
            {processedData.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={8}>
                  {isArabic ? "لا توجد بيانات" : "No data found"}
                </Td>
              </Tr>
            ) : (
              processedData.map((row, idx) => {
                const isSelected = selectedRowIndex === idx;
                return (
                  <React.Fragment key={idx}>
                    <Tr
                      bg={
                        isSelected
                          ? isDark
                            ? "rgba(99, 102, 241, 0.22)"
                            : "rgba(99, 102, 241, 0.10)"
                          : idx % 2 === 1
                            ? isDark ? "rgba(255,255,255,0.04)" : "#F1F5F9"
                            : "transparent"
                      }
                      boxShadow={isSelected ? `inset 3px 0 0 ${colors.mainColor}` : "none"}
                      outline={isSelected ? `1.5px solid ${colors.mainColor}` : "none"}
                      outlineOffset="-1.5px"
                      transition="all 0.18s"
                      cursor={onRowClick ? "pointer" : "default"}
                      _hover={{
                        bg: isSelected
                          ? isDark ? "rgba(99,102,241,0.30)" : "rgba(99,102,241,0.16)"
                          : isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0",
                      }}
                      onClick={() => onRowClick && onRowClick(row, idx)}
                    >
                      {renderRow(row, idx, columns)}
                    </Tr>
                    {renderExpandedRow && renderExpandedRow(row, idx, columns)}
                  </React.Fragment>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
